// ============================================================
// M8.5 A-07 · 相机标定向导（2D→3D 地面单应）
// 步骤：选择相机 → 取快照 → 画面点选 + 填写场景坐标（≥4 组）→ 求解（RMS）→ 保存
// 说明：场景坐标 = 数字孪生场景世界坐标（米，x 东 / z 北，y=0 地面）
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Select, InputNumber, Button, Space, Table, Tag, message, Alert, Popconfirm } from 'antd';
import { AimOutlined } from '@ant-design/icons';

interface CameraItem {
  id: string;
  imei: string;
  deviceName?: string | null;
  calibrated?: boolean;
  calibrationRms?: number | null;
}

interface CalibPoint {
  img: { x: number; y: number };
  scene: { x: number; y: number };
}

interface CalibrationData {
  H: number[];
  rms: number;
  points: CalibPoint[];
  calibratedAt: string;
}

const API = '/api/v1';

function authHeaders(): Record<string, string> {
  try {
    const token = localStorage.getItem('dtwin_token') || localStorage.getItem('token') || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${API}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(init?.headers || {}) },
  });
  if (!resp.ok) {
    let msg = `请求失败 ${resp.status}`;
    try {
      const j = (await resp.json()) as { message?: string | string[] };
      if (j?.message) msg = Array.isArray(j.message) ? j.message.join('；') : j.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const j = (await resp.json()) as { data?: T } & T;
  return (j && typeof j === 'object' && 'data' in j ? (j.data as T) : (j as T));
}

export default function CameraCalibrationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [cameras, setCameras] = useState<CameraItem[]>([]);
  const [cameraId, setCameraId] = useState<string>('');
  /** 相机服务地址覆盖（可选）；留空则由服务端从设备备注推导 */
  const [cameraBase, setCameraBase] = useState<string>('');
  const [snapshotUrl, setSnapshotUrl] = useState<string>('');
  const [natural, setNatural] = useState<{ w: number; h: number }>({ w: 1280, h: 960 });
  const [points, setPoints] = useState<CalibPoint[]>([]);
  const [solved, setSolved] = useState<{ rms: number } | null>(null);
  const [existing, setExisting] = useState<CalibrationData | null>(null);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const objectUrlRef = useRef<string>('');

  const loadCameras = useCallback(async () => {
    try {
      const r = await apiJson<{ items: CameraItem[] }>('/rtu/devices/cameras');
      setCameras(r.items || []);
    } catch (e) {
      message.error(`相机列表加载失败：${(e as Error).message}`);
    }
  }, []);

  const loadCalibration = useCallback(async (id: string) => {
    if (!id) return;
    try {
      const data = await apiJson<CalibrationData | null>(`/rtu/devices/${id}/calibration`);
      setExisting(data);
      setPoints(data?.points || []);
      setSolved(data ? { rms: data.rms } : null);
    } catch {
      setExisting(null);
    }
  }, []);

  const loadSnapshot = useCallback(async (id: string, base?: string) => {
    if (!id) return;
    try {
      const q = base?.trim() ? `?base=${encodeURIComponent(base.trim())}` : '';
      const resp = await fetch(`${API}/rtu/devices/${id}/snapshot${q}`, { headers: authHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setSnapshotUrl(url);
    } catch (e) {
      setSnapshotUrl('');
      message.warning(`快照获取失败：${(e as Error).message}`);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void loadCameras();
    } else {
      setSolved(null);
    }
  }, [open, loadCameras]);

  useEffect(() => {
    if (open && cameraId) {
      void loadCalibration(cameraId);
      void loadSnapshot(cameraId, cameraBase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cameraId, loadCalibration, loadSnapshot]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * natural.w);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * natural.h);
    setPoints((prev) => [...prev, { img: { x, y }, scene: { x: 0, y: 0 } }]);
    setSolved(null);
    message.success(`已添加图像点 (${x}, ${y})，请在下方填写对应场景坐标`);
  };

  const updateScene = (idx: number, axis: 'x' | 'y', v: number | null) => {
    setPoints((prev) => prev.map((p, i) => (i === idx ? { ...p, scene: { ...p.scene, [axis]: v ?? 0 } } : p)));
    setSolved(null);
  };

  const removePoint = (idx: number) => {
    setPoints((prev) => prev.filter((_, i) => i !== idx));
    setSolved(null);
  };

  const doSolve = async () => {
    setBusy(true);
    try {
      const r = await apiJson<{ H: number[]; rms: number }>(`/rtu/devices/${cameraId}/calibration/solve`, {
        method: 'POST',
        body: JSON.stringify({ points }),
      });
      setSolved({ rms: r.rms });
      message.success(`求解成功：重投影误差 ${r.rms.toFixed(3)} m`);
    } catch (e) {
      message.error(`求解失败：${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const doSave = async () => {
    setBusy(true);
    try {
      const data = await apiJson<CalibrationData>(`/rtu/devices/${cameraId}/calibration`, {
        method: 'PUT',
        body: JSON.stringify({ points }),
      });
      setExisting(data);
      setSolved({ rms: data.rms });
      message.success('标定已保存：AI 检测事件将携带 scenePos 空间坐标');
      void loadCameras();
    } catch (e) {
      message.error(`保存失败：${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const doClear = async () => {
    setBusy(true);
    try {
      await apiJson(`/rtu/devices/${cameraId}/calibration`, { method: 'DELETE' });
      setExisting(null);
      setPoints([]);
      setSolved(null);
      message.success('标定已清除');
      void loadCameras();
    } catch (e) {
      message.error(`清除失败：${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span>
          <AimOutlined /> 相机标定（2D 画面 → 3D 场景地面）
        </span>
      }
      width={860}
      footer={null}
      destroyOnHidden
    >
      <Space orientation="vertical" style={{ width: '100%' }} size={10}>
        <Space wrap>
          <span style={{ fontSize: 13 }}>相机：</span>
          <Select
            data-testid="calib-camera-select"
            style={{ width: 320 }}
            placeholder="选择已接入的 AI 相机"
            value={cameraId || undefined}
            onChange={setCameraId}
            options={cameras.map((c) => ({
              value: c.id,
              label: `${c.deviceName || c.imei}${c.calibrated ? `（已标定 RMS ${c.calibrationRms ?? '-'}m）` : '（未标定）'}`,
            }))}
          />
          {cameraId ? (
            <Space.Compact>
              <input
                data-testid="calib-base-input"
                value={cameraBase}
                onChange={(e) => setCameraBase(e.target.value)}
                placeholder="相机服务地址（可选，如 http://192.168.1.252:8080）"
                style={{ width: 260, padding: '3px 8px', borderRadius: 6, border: '1px solid #d9d9d9', fontSize: 12 }}
              />
              <Button size="small" onClick={() => loadSnapshot(cameraId, cameraBase)}>
                刷新快照
              </Button>
            </Space.Compact>
          ) : null}
          {existing ? (
            <Tag color="green">
              已标定 · {existing.calibratedAt} · RMS {existing.rms}m
            </Tag>
          ) : cameraId ? (
            <Tag>未标定</Tag>
          ) : null}
        </Space>

        <Alert
          type="info"
          showIcon
          message="标定方法：在画面中点击 ≥4 个地面参考点（分布尽量覆盖画面），并填写它们在场景中的世界坐标（米）。求解后重投影误差（RMS）越小越好（建议 < 0.3m）。"
        />

        {snapshotUrl ? (
          <div style={{ position: 'relative', maxHeight: 420, overflow: 'auto', background: '#000', borderRadius: 6 }}>
            <img
              ref={imgRef}
              data-testid="calib-snapshot"
              src={snapshotUrl}
              alt="相机快照"
              style={{ width: '100%', display: 'block', cursor: 'crosshair' }}
              onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              onClick={handleImageClick}
            />
            {points.map((p, i) => (
              <div
                key={`${p.img.x}-${p.img.y}-${i}`}
                style={{
                  position: 'absolute',
                  left: `${(p.img.x / natural.w) * 100}%`,
                  top: `${(p.img.y / natural.h) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  background: '#22d3ee',
                  color: '#083344',
                  borderRadius: '50%',
                  width: 18,
                  height: 18,
                  lineHeight: '18px',
                  textAlign: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  border: '1px solid #fff',
                }}
              >
                {i + 1}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', background: '#0f172a', borderRadius: 6 }}>
            {cameraId ? '快照加载中或失败（请检查设备备注中的相机地址，或点「刷新快照」）' : '请先选择相机'}
          </div>
        )}

        {points.length > 0 ? (
          <Table<CalibPoint>
            size="small"
            rowKey={(_, i) => String(i)}
            dataSource={points}
            pagination={false}
            scroll={{ y: 160 }}
            columns={[
              { title: '#', width: 40, render: (_v, _r, i) => i + 1 },
              { title: '图像 x', width: 90, render: (_v, p) => Math.round(p.img.x) },
              { title: '图像 y', width: 90, render: (_v, p) => Math.round(p.img.y) },
              {
                title: '场景 X（米）',
                render: (_v, p, i) => (
                  <InputNumber
                    data-testid={`calib-point-x-${i}`}
                    size="small"
                    value={p.scene.x}
                    step={0.5}
                    onChange={(v) => updateScene(i, 'x', v)}
                  />
                ),
              },
              {
                title: '场景 Z（米）',
                render: (_v, p, i) => (
                  <InputNumber
                    data-testid={`calib-point-z-${i}`}
                    size="small"
                    value={p.scene.y}
                    step={0.5}
                    onChange={(v) => updateScene(i, 'y', v)}
                  />
                ),
              },
              {
                title: '',
                width: 60,
                render: (_v, _r, i) => (
                  <Button type="link" danger size="small" onClick={() => removePoint(i)}>
                    删除
                  </Button>
                ),
              },
            ]}
          />
        ) : null}

        <Space wrap>
          <Button data-testid="calib-solve" type="primary" disabled={!cameraId || points.length < 4 || busy} onClick={doSolve}>
            求解（{points.length} 点）
          </Button>
          <Button data-testid="calib-save" disabled={!solved || busy} onClick={doSave}>
            保存标定
          </Button>
          {existing ? (
            <Popconfirm title="清除该相机标定？" onConfirm={doClear}>
              <Button danger disabled={busy}>
                清除
              </Button>
            </Popconfirm>
          ) : null}
          {solved ? (
            <Tag data-testid="calib-rms" color={solved.rms < 0.3 ? 'green' : 'orange'}>
              重投影 RMS {solved.rms.toFixed(3)} m
            </Tag>
          ) : null}
        </Space>
      </Space>
    </Modal>
  );
}
